/**
 * Phase 3.45 — Wallet / Guthaben-Verwendung Section (User-Dashboard).
 *
 * Zeigt das Guthaben (€Punkte → € umgerechnet, minus Verwendungen) und bietet
 * 6 Optionen: Sparen+Zinsen, Firmenanteile, Verrechnung (Standard), Kaution,
 * Spenden, Auszahlen.
 */
import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Wallet, PiggyBank, Building2, Coins, ShieldCheck, Heart, Banknote, Loader2, TrendingUp, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const ACTION_META = {
    savings_deposit: { label: "Sparen einzahlen", icon: PiggyBank, tone: "emerald" },
    savings_withdraw: { label: "Sparen abheben", icon: RefreshCw, tone: "emerald" },
    savings_interest: { label: "Zins-Gutschrift", icon: TrendingUp, tone: "emerald" },
    shares_purchase: { label: "Anteile gekauft", icon: Building2, tone: "violet" },
    kaution_deposit: { label: "Kaution hinterlegt", icon: ShieldCheck, tone: "blue" },
    kaution_release: { label: "Kaution freigegeben", icon: ShieldCheck, tone: "blue" },
    donation: { label: "Spende", icon: Heart, tone: "rose" },
    payout_request: { label: "Auszahlung", icon: Banknote, tone: "amber" },
};

function eur(n) {
    return new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(n || 0);
}

function StatTile({ icon: Icon, label, value, testId, tone = "yellow" }) {
    const tones = {
        yellow: "bg-[#FFFBEB] border-[#FCD34D] text-[#78350F]",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
        violet: "bg-violet-50 border-violet-200 text-violet-900",
        blue: "bg-blue-50 border-blue-200 text-blue-900",
        rose: "bg-rose-50 border-rose-200 text-rose-900",
        amber: "bg-amber-50 border-amber-200 text-amber-900",
    };
    return (
        <div className={`border rounded-sm p-4 ${tones[tone] || tones.yellow}`} data-testid={testId}>
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider mb-1">
                <Icon className="w-3.5 h-3.5" /> {label}
            </div>
            <div className="font-display font-black text-2xl font-mono-num">{value}</div>
        </div>
    );
}

function ActionCard({ title, description, icon: Icon, children, testId, accent = "yellow" }) {
    const accents = {
        yellow: "border-[#FCD34D]",
        emerald: "border-emerald-300",
        violet: "border-violet-300",
        blue: "border-blue-300",
        rose: "border-rose-300",
        amber: "border-amber-300",
    };
    return (
        <div className={`bg-white border-2 ${accents[accent] || accents.yellow} rounded-sm p-5`} data-testid={testId}>
            <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 bg-[#FFCC00] rounded-sm flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#111827]" />
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-display font-black text-base">{title}</h4>
                    <p className="text-xs text-gray-600 mt-1 leading-relaxed">{description}</p>
                </div>
            </div>
            {children}
        </div>
    );
}

export function WalletSection({ currentUser, embedded = false }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [amounts, setAmounts] = useState({
        savings_deposit: "", savings_withdraw: "", shares_purchase: "",
        kaution_deposit: "", donation: "", payout_request: "",
    });
    const role = currentUser?.role;

    const load = useCallback(async () => {
        try {
            const r = await api.get("/wallet/me");
            setData(r.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Wallet konnte nicht geladen werden");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const setAmt = (k, v) => setAmounts((p) => ({ ...p, [k]: v }));

    const submit = async (action) => {
        const amt = parseFloat(amounts[action]);
        if (!amt || amt <= 0) { toast.error("Bitte einen gültigen Betrag eingeben"); return; }
        setBusy(true);
        try {
            await api.post("/wallet/me/action", { action, amount_eur: amt });
            toast.success("Aktion durchgeführt");
            setAmt(action, "");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    if (!data) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-8 flex items-center gap-3 text-gray-500" data-testid="wallet-section-loading">
                <Loader2 className="w-5 h-5 animate-spin" /> Lade Guthaben…
            </div>
        );
    }

    const { balance, config, recent_transactions, has_bank_iban } = data;
    const cfg = config || {};
    const yearlyInterest = (balance.savings_balance_eur * (cfg.interest_rate_yearly_pct || 0) / 100).toFixed(2);
    const equityProgress = Math.min(100, Math.round((balance.shares_tokens / (cfg.shares_equity_threshold_tokens || 1)) * 100));

    return (
        <div className={embedded ? "" : "bg-white border border-gray-200 rounded-sm"} data-testid="wallet-section">
            {!embedded && (
                <div className="px-6 py-4 border-b border-gray-200 bg-[#FFFBEB]/40 flex items-center gap-3">
                    <Wallet className="w-5 h-5 text-[#FFCC00] shrink-0" />
                    <div className="flex-1 min-w-0">
                        <h3 className="font-display font-bold text-lg">Mein Guthaben</h3>
                        <p className="text-xs text-gray-600 mt-0.5">Verwenden Sie Ihr aus €Punkten erwirtschaftetes Guthaben — sparen, investieren, spenden oder auszahlen.</p>
                    </div>
                </div>
            )}
            <div className={embedded ? "space-y-6" : "p-6 space-y-6"}>
                {/* Balance Übersicht */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <StatTile icon={Wallet} label="Verfügbar" value={eur(balance.available_eur)} testId="wallet-available" tone="yellow" />
                    <StatTile icon={PiggyBank} label="Sparen" value={eur(balance.savings_balance_eur)} testId="wallet-savings" tone="emerald" />
                    <StatTile icon={Building2} label="Anteile (Tokens)" value={balance.shares_tokens} testId="wallet-shares" tone="violet" />
                    <StatTile icon={ShieldCheck} label="Aktive Kaution" value={eur(balance.kaution_active_eur)} testId="wallet-kaution" tone="blue" />
                </div>

                {/* Actions Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* 1) Sparen */}
                    <ActionCard
                        title="1. Sparen & verzinsen"
                        description={`Aktuell ${cfg.interest_rate_yearly_pct ?? 2}% p.a. ab ${eur(cfg.interest_min_savings_eur ?? 10)} Mindest-Saldo. Lock-Up ${cfg.savings_min_lock_days ?? 30} Tage. Voraussichtlicher Jahreszins: ${eur(yearlyInterest)}.`}
                        icon={PiggyBank}
                        testId="wallet-action-savings"
                        accent="emerald"
                    >
                        <div className="flex gap-2">
                            <Input
                                type="number" min="1" step="1"
                                value={amounts.savings_deposit}
                                onChange={(e) => setAmt("savings_deposit", e.target.value)}
                                placeholder="Einzahlen €"
                                className="rounded-sm h-9 font-mono-num text-sm"
                                data-testid="wallet-savings-deposit-input"
                            />
                            <Button onClick={() => submit("savings_deposit")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-9 px-3 text-xs font-bold uppercase tracking-wider" data-testid="wallet-savings-deposit-btn">
                                Einzahlen
                            </Button>
                        </div>
                        <div className="flex gap-2 mt-2">
                            <Input
                                type="number" min="1" step="1"
                                value={amounts.savings_withdraw}
                                onChange={(e) => setAmt("savings_withdraw", e.target.value)}
                                placeholder="Abheben €"
                                className="rounded-sm h-9 font-mono-num text-sm"
                                data-testid="wallet-savings-withdraw-input"
                            />
                            <Button onClick={() => submit("savings_withdraw")} disabled={busy} variant="outline" className="border-emerald-600 text-emerald-700 rounded-sm h-9 px-3 text-xs font-bold uppercase tracking-wider" data-testid="wallet-savings-withdraw-btn">
                                Abheben
                            </Button>
                        </div>
                    </ActionCard>

                    {/* 2) Firmenanteile */}
                    <ActionCard
                        title="2. Firmenanteile erwerben"
                        description={`${eur(cfg.shares_token_eur_per_token ?? 1)} pro Token. Ab ${cfg.shares_equity_threshold_tokens ?? 1000} Tokens werden Sie für echte Unternehmensanteile freigeschaltet. Max ${cfg.shares_max_tokens_per_user ?? 10000} pro User.`}
                        icon={Building2}
                        testId="wallet-action-shares"
                        accent="violet"
                    >
                        <div className="mb-2">
                            <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                                <span>Equity-Fortschritt</span><span className="font-mono-num">{balance.shares_tokens} / {cfg.shares_equity_threshold_tokens ?? 1000}</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-sm overflow-hidden">
                                <div className="h-full bg-violet-500 transition-all" style={{ width: `${equityProgress}%` }} data-testid="wallet-shares-progress" />
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <Input
                                type="number" min="1" step="1"
                                value={amounts.shares_purchase}
                                onChange={(e) => setAmt("shares_purchase", e.target.value)}
                                placeholder="Investieren €"
                                className="rounded-sm h-9 font-mono-num text-sm"
                                data-testid="wallet-shares-input"
                            />
                            <Button onClick={() => submit("shares_purchase")} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white rounded-sm h-9 px-3 text-xs font-bold uppercase tracking-wider" data-testid="wallet-shares-btn">
                                Investieren
                            </Button>
                        </div>
                    </ActionCard>

                    {/* 3) Verrechnung */}
                    <ActionCard
                        title="3. Stehen lassen (Verrechnung)"
                        description={`Standard-Verhalten: Verfügbares Guthaben wird automatisch mit Tarif, Werkstatt-Rechnungen und Fahrzeugkäufen verrechnet — Priorität: ${cfg.verrechnung_priority_label || "Standard"}.`}
                        icon={Coins}
                        testId="wallet-action-verrechnung"
                        accent="yellow"
                    >
                        <div className="text-xs text-gray-700 bg-[#FFFBEB] border border-[#FCD34D] rounded-sm p-3" data-testid="wallet-verrechnung-info">
                            ✓ Aktiv — keine Aktion erforderlich. Ihr verfügbares Guthaben <strong className="font-mono-num">{eur(balance.available_eur)}</strong> wird automatisch eingesetzt.
                        </div>
                    </ActionCard>

                    {/* 4) Kaution */}
                    <ActionCard
                        title="4. Kaution für Transaktionen"
                        description={`Hinterlegen Sie eine Kaution (${cfg.kaution_required_pct ?? 10}% empfohlen) für sichere Käufe. Freigabe nach ${cfg.kaution_release_days ?? 30} Tagen automatisch oder manuell durch Admin.`}
                        icon={ShieldCheck}
                        testId="wallet-action-kaution"
                        accent="blue"
                    >
                        <div className="flex gap-2">
                            <Input
                                type="number" min="1" step="1"
                                value={amounts.kaution_deposit}
                                onChange={(e) => setAmt("kaution_deposit", e.target.value)}
                                placeholder="Hinterlegen €"
                                className="rounded-sm h-9 font-mono-num text-sm"
                                data-testid="wallet-kaution-input"
                            />
                            <Button onClick={() => submit("kaution_deposit")} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm h-9 px-3 text-xs font-bold uppercase tracking-wider" data-testid="wallet-kaution-btn">
                                Hinterlegen
                            </Button>
                        </div>
                    </ActionCard>

                    {/* 5) Spenden */}
                    <ActionCard
                        title="5. Spenden für Weiterentwicklung"
                        description={`Min. ${eur(cfg.donation_min_eur ?? 1)} pro Spende. Bisher gespendet: ${eur(balance.donation_total_eur)}. Vielen Dank für Ihre Unterstützung!`}
                        icon={Heart}
                        testId="wallet-action-donation"
                        accent="rose"
                    >
                        <div className="flex gap-2">
                            <Input
                                type="number" min="1" step="1"
                                value={amounts.donation}
                                onChange={(e) => setAmt("donation", e.target.value)}
                                placeholder="Spenden €"
                                className="rounded-sm h-9 font-mono-num text-sm"
                                data-testid="wallet-donation-input"
                            />
                            <Button onClick={() => submit("donation")} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white rounded-sm h-9 px-3 text-xs font-bold uppercase tracking-wider" data-testid="wallet-donation-btn">
                                Spenden
                            </Button>
                        </div>
                    </ActionCard>

                    {/* 6) Auszahlen */}
                    <ActionCard
                        title="6. Auszahlen auf Bankkonto"
                        description={`Mindestbetrag: ${eur(cfg.payout_min_eur ?? 20)} · Gebühr: ${eur(cfg.payout_fee_eur ?? 0)} · Bearbeitungszeit: ${cfg.payout_processing_days ?? 14} Tage. Bereits angefragt: ${eur(balance.payout_pending_eur)}.`}
                        icon={Banknote}
                        testId="wallet-action-payout"
                        accent="amber"
                    >
                        {!has_bank_iban ? (
                            <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="wallet-payout-no-iban">
                                ⚠ Bitte zuerst Ihre IBAN unter <strong>Finanzen → Bankverbindung</strong> hinterlegen.
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <Input
                                    type="number" min="1" step="1"
                                    value={amounts.payout_request}
                                    onChange={(e) => setAmt("payout_request", e.target.value)}
                                    placeholder="Auszahlen €"
                                    className="rounded-sm h-9 font-mono-num text-sm"
                                    data-testid="wallet-payout-input"
                                />
                                <Button onClick={() => submit("payout_request")} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm h-9 px-3 text-xs font-bold uppercase tracking-wider" data-testid="wallet-payout-btn">
                                    Auszahlen
                                </Button>
                            </div>
                        )}
                    </ActionCard>
                </div>

                {/* Recent transactions */}
                {recent_transactions && recent_transactions.length > 0 && (
                    <div data-testid="wallet-recent">
                        <h4 className="font-bold text-sm uppercase tracking-wider text-gray-700 mb-2">Letzte Bewegungen</h4>
                        <div className="space-y-1 max-h-72 overflow-y-auto">
                            {recent_transactions.map((t) => {
                                const meta = ACTION_META[t.action] || { label: t.action, icon: Coins };
                                const Icon = meta.icon;
                                return (
                                    <div key={t.id} className="flex items-center gap-3 p-2 border border-gray-100 rounded-sm bg-gray-50 text-xs" data-testid={`wallet-tx-${t.id}`}>
                                        <Icon className="w-4 h-4 text-gray-500 shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold">{meta.label}{t.tokens ? ` · ${t.tokens} Tokens` : ""}</div>
                                            <div className="text-[10px] text-gray-500">{new Date(t.created_at).toLocaleString("de-DE")} · {t.status}</div>
                                        </div>
                                        <div className="font-mono-num font-bold tabular-nums">{eur(t.amount_eur)}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
